import type { FacilityActivity, FacilityActivityBehavior, FacilityActivityEntry } from '../index.js'

const DAY_MS = 24 * 60 * 60 * 1000
/** The steps Show earlier takes; the last is the furthest back the API reads. */
const EARLIER_STEPS_DAYS = [7, 30] as const
export const MESSAGES_FILTER = 'messages'

export function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
}

/** A shift's calendar date as written (`YYYY-MM-DD`), read at noon so no time zone moves it to the day before. */
export function formatShiftDate(date: string | null | undefined): string {
  if (!date) {
    return 'a'
  }
  const parsed = new Date(`${date}T12:00:00`)
  return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
}

/** What the window covers, in the reader's terms. */
export function windowLabel(activity: Pick<FacilityActivity, 'since' | 'basis' | 'capped' | 'previousSignInAt'>): string {
  if (activity.basis === 'last_sign_in') {
    return activity.capped && activity.previousSignInAt ? `Last 30 days · you last signed in ${formatWhen(activity.previousSignInAt)}` : `Since you last signed in · ${formatWhen(activity.since)}`
  }
  const days = Math.round((Date.now() - Date.parse(activity.since)) / DAY_MS)
  return activity.basis === 'default_window' || days >= 1 ? `Last ${days} day${days === 1 ? '' : 's'}` : `Since ${formatWhen(activity.since)}`
}

/** The next earlier start to offer, or null when the window already reaches as far back as the API reads. */
export function earlierSince(currentSince: string, now: number = Date.now()): { since: string; days: number } | null {
  const currentDays = (now - Date.parse(currentSince)) / DAY_MS
  // Half a day of slack: a 7-day window read a moment ago is not "less than 7 days".
  const days = EARLIER_STEPS_DAYS.find((step) => step > currentDays + 0.5)
  return days === undefined ? null : { since: new Date(now - days * DAY_MS).toISOString(), days }
}

export type ActivityTone = 'good' | 'concern' | 'neutral'

export interface ActivityLine {
  /** What happened, after the worker's name: "called out of the Thu 8 Oct shift". */
  text: string
  /** A second line: the unit, a note, the words of a message. */
  detail: string | null
  tone: ActivityTone
}

const NO_UNITS: ReadonlyMap<string, string> = new Map()

/**
 * A unit as the facility calls it, never its id (26 October 2026: "Unit unit-b"); a unit the facility no longer lists,
 * or one read before its names arrive, keeps its id, with "Unit" in front only where the id does not already say it.
 */
function unitText(unitId: string, units: ReadonlyMap<string, string>): string {
  return units.get(unitId) ?? (/^unit\b/i.test(unitId) ? unitId : `Unit ${unitId}`)
}

/** One entry in words. `behaviors` names a custom behavior and says which way it counts; `units` names the units. */
export function describeEntry(entry: FacilityActivityEntry, behaviors: ReadonlyMap<string, FacilityActivityBehavior>, units: ReadonlyMap<string, string> = NO_UNITS): ActivityLine {
  const shift = `${formatShiftDate(entry.shiftDate)} shift`
  const unit = entry.unitId ? unitText(entry.unitId, units) : null
  switch (entry.kind) {
    case 'callout':
      return { text: `called out of the ${shift}`, detail: [unit, entry.reportedAt ? `notice given ${formatWhen(entry.reportedAt)}` : null].filter(Boolean).join(' · ') || null, tone: 'concern' }
    case 'no_show':
      return { text: `did not show for the ${shift}`, detail: unit, tone: 'concern' }
    case 'shift_completed': {
      const shortfalls = [entry.onTime === false ? 'arrived late' : null, entry.workedToEnd === false ? 'left before the end' : null, entry.chartCompleted === false ? 'did not complete the chart' : null].filter(Boolean)
      // Said only when the shift reported its chart: one shift, one chart.
      const charted = entry.chartCompleted === true ? ', chart completed' : ''
      return shortfalls.length > 0
        ? { text: `worked the ${shift}, ${joinPhrases(shortfalls as string[])}`, detail: unit, tone: 'concern' }
        : { text: `worked the ${shift}, on time and to the end${charted}`, detail: unit, tone: 'good' }
    }
    case 'behavior': {
      const behavior = entry.behaviorKey ? behaviors.get(entry.behaviorKey) : undefined
      return { text: `${behavior?.label ?? entry.behaviorKey ?? 'Behavior'} reported`, detail: entry.note ?? null, tone: behavior?.direction === 'negative' ? 'concern' : behavior ? 'good' : 'neutral' }
    }
    case 'message':
      return { text: 'was sent a message', detail: entry.text ? `“${entry.text}”` : null, tone: 'neutral' }
    case 'bridge_opened': {
      const told = entry.poolSize === 1 ? '1 enrolled worker told' : `${entry.poolSize ?? 0} enrolled workers told`
      const when = `${shift}, ${entry.scheduledStart ?? ''}–${entry.scheduledEnd ?? ''}`
      // With a worker named, the line reads after their name; without one, it stands alone.
      return entry.workerId ? { text: `called out of the ${when}; a Clinical Bridge callout went out, ${told}`, detail: [unit, entry.reason].filter(Boolean).join(' · ') || null, tone: 'neutral' } : { text: `Clinical Bridge callout opened for the ${when}, ${told}`, detail: [unit, entry.reason].filter(Boolean).join(' · ') || null, tone: 'neutral' }
    }
    case 'bridge_filled': {
      const when = `${shift}, ${entry.scheduledStart ?? ''}–${entry.scheduledEnd ?? ''}`
      const minutes = entry.responseSeconds === undefined ? null : Math.round(entry.responseSeconds / 60)
      const after = minutes === null ? '' : minutes < 1 ? ', within a minute of the alert' : `, ${minutes} min after the alert`
      return { text: `took the Clinical Bridge ${when}${after}`, detail: unit, tone: 'good' }
    }
    case 'staff_message': {
      const who = entry.sentBy ? `${entry.sentBy} wrote` : 'Staff wrote'
      const to = entry.toKind === 'roster' ? `to ${entry.count ?? 0} ${entry.count === 1 ? 'worker' : 'workers'} on the roster` : entry.workerId ? '' : `to ${entry.count ?? 0} workers`
      return entry.workerId ? { text: `was sent a message by ${entry.sentBy ?? 'staff'}${entry.urgent ? ', marked urgent' : ''}`, detail: null, tone: 'neutral' } : { text: `${who} ${to}${entry.urgent ? ', marked urgent' : ''}`, detail: null, tone: 'neutral' }
    }
    case 'schedule_released': {
      const month = entry.period ? new Date(`${entry.period}-01T00:00:00Z`).toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }) : 'The month'
      const unfilled = entry.shiftsUnfilled ? `${entry.shiftsUnfilled} ${entry.shiftsUnfilled === 1 ? 'seat' : 'seats'} left to the open board` : 'every seat filled'
      return { text: `${month} was released to ${entry.workers ?? 0} ${entry.workers === 1 ? 'worker' : 'workers'}: ${unfilled}`, detail: entry.overrode ? `released past ${entry.overrode.findings === 1 ? 'a block' : `${entry.overrode.findings} blocks`} · ${entry.overrode.reasonCode}` : null, tone: entry.overrode ? 'concern' : 'good' }
    }
    case 'census_applied': {
      const did = [entry.floated ? `${entry.floated} floated to another unit` : null, entry.flexed ? `${entry.flexed} released, lowest tier first` : null, entry.recalled ? `${entry.recalled} offered a seat back` : null, entry.posted ? `${entry.posted} ${entry.posted === 1 ? 'seat' : 'seats'} posted to the open board` : null].filter(Boolean) as string[]
      const how = entry.by === 'applied' ? 'applied by staff' : entry.by === 'clock' ? 'by the hourly clock' : 'automatic'
      return { text: `Census ${entry.verdict === 'fall' ? 'fell' : 'rose'} to ${entry.census ?? '?'} for the ${shift}: ${did.length > 0 ? joinPhrases(did) : 'nothing to change'}`, detail: [unit, how].filter(Boolean).join(' · '), tone: entry.verdict === 'fall' ? 'concern' : 'neutral' }
    }
    case 'schedule_board':
      return describeBoardEntry(entry, units)
  }
}

const BOARD_SOURCE: Record<string, string> = { unfilled: 'left open by the build', vacated: 'vacated', bridge_callout: 'escalated from a Clinical Bridge callout', census_rise: 'added for a census rise', demo: 'played for the two-minute demo' }
const PICKUP_MODE: Record<string, string> = { offer: 'from an offer', first_come: 'first come', override: "by a supervisor's override", bridge: 'through a Clinical Bridge callout' }
const WITHDRAWN_REASON: Record<string, string> = { census_fall: 'the census fell', unpublished: 'the shift was unpublished', demo: 'the demo seat was taken down' }

/** A move of the schedule board in words: the seat and what happened to it, or the swap. */
function describeBoardEntry(entry: FacilityActivityEntry, units: ReadonlyMap<string, string>): ActivityLine {
  const seat = entry.shift ? `${formatShiftDate(entry.shift.date)} ${entry.shift.shiftType}${entry.shift.role ? ` ${entry.shift.role}` : ''} seat` : 'seat'
  const unit = entry.shift ? unitText(entry.shift.unitId, units) : null
  const line = describeBoardMove(entry, seat, unit)
  // The seat's own account of the move rides as the line's detail where the entry carries it (seat life reasons plan D4), so the feed and the seat's life agree.
  return entry.why ? { ...line, detail: [unit, entry.why].filter(Boolean).join(' · ') } : line
}

function describeBoardMove(entry: FacilityActivityEntry, seat: string, unit: string | null): ActivityLine {
  switch (entry.event) {
    case 'posted':
      return entry.workerId ? { text: `vacated the ${seat}; it went to the open board`, detail: unit, tone: 'concern' } : { text: `The ${seat} went to the open board, ${BOARD_SOURCE[entry.source ?? ''] ?? 'open'}`, detail: unit, tone: 'neutral' }
    case 'blasted':
      return { text: `The ${seat} was put to everyone eligible, first come`, detail: unit, tone: 'neutral' }
    case 'filled':
      return { text: `took the ${seat} ${PICKUP_MODE[entry.mode ?? ''] ?? ''}`.trim(), detail: unit, tone: 'good' }
    case 'ran_short':
      return { text: `The ${seat} was run short by decision`, detail: [unit, entry.reasonCode].filter(Boolean).join(' · ') || null, tone: 'concern' }
    case 'withdrawn':
      return { text: `The ${seat} was withdrawn: ${WITHDRAWN_REASON[entry.reasonCode ?? ''] ?? 'no longer needed'}`, detail: unit, tone: 'neutral' }
    case 'ended_unfilled':
      return { text: `The ${seat} started with nobody in it: ended unfilled`, detail: unit, tone: 'concern' }
    case 'swap_completed':
      return { text: 'swapped a shift', detail: null, tone: 'neutral' }
    case 'swap_denied':
      return { text: 'had a swap denied', detail: null, tone: 'concern' }
    default:
      return { text: 'The schedule board changed', detail: unit, tone: 'neutral' }
  }
}

/** "a", "a and b", "a, b and c". */
function joinPhrases(phrases: readonly string[]): string {
  return phrases.length <= 1 ? phrases.join('') : `${phrases.slice(0, -1).join(', ')} and ${phrases[phrases.length - 1]}`
}
